import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { StringParam, useQueryParam } from "use-query-params";

import { isJenkinsCustomEnv, isLocalHostMode } from "../helpers";
import {
  DEBUG_MODE_LEVEL_FULL,
  DEBUG_MODE_QUERY_PARAM_KEY,
  DEBUG_MODE_RIDE_CARD_MAP_TOOLBOX_LEVEL,
  debugModeLevelQueryParam,
  isJenkinsTestPathname,
  isNonProductionNavbarColorFromEnv,
  parseSearchHasDebugModeQueryParam,
  resolveSearchStringForDebugModePresence,
} from "./parts";

export const useDebugMode = () => {
  const [debugModeQs, setDebugModeQs] = useQueryParam(
    DEBUG_MODE_QUERY_PARAM_KEY,
    debugModeLevelQueryParam,
  );
  const location = useLocation();

  const debugModeLevel =
    debugModeQs === null || debugModeQs === undefined ? undefined : debugModeQs;

  const debugMode = useMemo(
    () => debugModeLevel !== undefined && debugModeLevel > 0,
    [debugModeLevel],
  );

  const hasDebugModeQueryParam = useMemo(() => {
    const search = resolveSearchStringForDebugModePresence(
      location.search ?? "",
    );
    return parseSearchHasDebugModeQueryParam(search);
  }, [location.search]);

  const isRideCardMapToolboxDebugMode = useMemo(
    () => debugModeLevel === DEBUG_MODE_RIDE_CARD_MAP_TOOLBOX_LEVEL,
    [debugModeLevel],
  );

  const isJenkinsTestMode = useMemo(() => {
    return isJenkinsTestPathname(location.pathname);
  }, [location.pathname]);

  const [debugModeCourseQs] = useQueryParam("debugModeCourse", StringParam);

  const hasDebugModeCourseQueryParam = Boolean(debugModeCourseQs);

  const isLocalOrDebugOrTestMode = useMemo(() => {
    return (
      isLocalHostMode() ||
      debugMode ||
      isJenkinsTestMode ||
      isJenkinsCustomEnv()
    );
  }, [debugMode, isJenkinsTestMode]);

  const isLocalOrDebugOrTestOrStagingMode = useMemo(() => {
    return isNonProductionNavbarColorFromEnv(location.search || location.hash);
    // pathname is read inside the helper from window.location
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when route path changes
  }, [location.search, location.hash, location.pathname]);

  // Same rules as login page; hook passes query-param values when inside `QueryParamProvider`.
  const isNonProductionNavbarColor = useMemo(() => {
    return isNonProductionNavbarColorFromEnv(location.search || location.hash);
    // pathname is read inside the helper from window.location
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when route path changes
  }, [location.search, location.hash, location.pathname]);

  const setDebugMode: (value: boolean | undefined) => void = useEvent(
    (value: boolean | undefined) => {
      /* Live-map diagnostics toggle: level 1 (synthetic map/grid previews, etc.). */
      const debugModeNormalized = value ? DEBUG_MODE_LEVEL_FULL : undefined;
      setDebugModeQs(debugModeNormalized);
    },
  );
  return {
    isLocalOrDebugOrTestMode,
    isLocalOrDebugOrTestOrStagingMode,
    isNonProductionNavbarColor,
    debugMode,
    debugModeLevel,
    hasDebugModeQueryParam,
    isRideCardMapToolboxDebugMode,
    setDebugMode,
    isJenkinsTestMode,
    hasDebugModeCourseQueryParam,
  };
};
